import LoadFailure from '../../components/Brand/LoadFailure';
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Layout } from 'antd';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AccountIcon, BanIcon, BackIcon, CloseIcon, EventIcon, NewsAddIcon } from '../../components/Brand/Icon';
import BrandLoader from '../../components/Brand/BrandLoader';
import ButtonPanelReaction from '../../components/Buttons/ButtonPanelReaction';
import UserImage from '../../components/UserImage';
import usePageLayout from '../../hooks/usePageLayout';
import useSearch from '../../hooks/useSearch';
import { isSearchableQuery, normalizeSearchQuery, SEARCH_MAX_LENGTH } from '../../services/search';
import { getLanguage, getUser } from '../../utils/func';
import { APP_ROUTES } from '../../utils/constant';
import url from '../../utils/url';
import { useBlockedUsers } from '../../blocking/hooks';
import '../../blocking/blocking.scss';
import './index.scss';

const { Header, Content, Footer } = Layout;

export default function SearchPage() {
  const { t, i18n } = useTranslation('brand');
  const { t: blocking } = useTranslation('blocking');
  const navigate = useNavigate();
  const location = useLocation();
  const parameters = new URLSearchParams(location.search);
  const query = parameters.get('q') || '';
  const kind = parameters.get('type') === 'users' ? 'users' : 'stories';
  const language = i18n.language || getLanguage();
  const blockedMode = kind === 'users' && parameters.get('blocked') === '1' && Boolean(getUser()?.id);
  const searchKey = `${language}:${kind}:${blockedMode}:${query}`;
  const [composing, setComposing] = useState(false);
  const header = useRef(null);
  const footer = useRef(null);
  const content = useRef(null);
  const input = useRef(null);
  const eventTab = useRef(null);
  const peopleTab = useRef(null);
  const tabId = useId();
  const initialFocus = useRef(Boolean(location.state?.fromSearchEntry) && location.state?.scrollTop == null);
  const restoreScroll = useRef({ key: searchKey, top: Math.max(0, Number(location.state?.scrollTop) || 0) });
  const { contentHeight, contentTop, keyboardOpen, headerHeight, footerHeight } = usePageLayout(header, footer);
  const publicSearch = useSearch({ kind, query, language, composing: composing || blockedMode });
  const blockedTooLong = Array.from(query).length > SEARCH_MAX_LENGTH;
  const blockedUsers = useBlockedUsers(kind === 'users' && Boolean(getUser()?.id), blockedMode ? query : '', blockedMode && (composing || blockedTooLong));
  const { items, status, error, hasMore, loadingMore, loadMore, retry } = blockedMode ? blockedUsers : publicSearch;
  const normalized = normalizeSearchQuery(query, kind);
  const queryLength = Array.from(normalized).length;
  const searchable = blockedMode ? !blockedTooLong : isSearchableQuery(query, kind);
  const validationHint = blockedMode && blockedTooLong && !composing ? 'searchTooLong' : !searchable && normalized && !composing
    ? queryLength > SEARCH_MAX_LENGTH ? 'searchTooLong'
      : queryLength < 2 ? 'searchShort' : 'searchInvalid'
    : null;

  useEffect(() => {
    if (!initialFocus.current) return;
    const frame = requestAnimationFrame(() => {
      initialFocus.current = false;
      input.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (!blockedMode || blockedUsers.status !== 'ready' || blockedUsers.count !== 0) return;
    const next = new URLSearchParams(location.search); next.delete('blocked');
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true, state: location.state });
  }, [blockedMode, blockedUsers.status, blockedUsers.count, location, navigate]);

  // Returning from a result restores the query and the exact list position.
  // If the cache has expired, load only the pages needed to reach that position.
  useLayoutEffect(() => {
    if (!content.current) return;
    if (restoreScroll.current.key !== searchKey) {
      restoreScroll.current = { key: searchKey, top: 0 };
      content.current.scrollTop = 0;
    }
    const top = restoreScroll.current.top;
    // The first render has not measured the fixed header/footer yet. Restoring
    // then would clamp the scroll to an oversized panel and discard its target.
    if (!top || status !== 'ready' || !headerHeight || (!keyboardOpen && !footerHeight)) return;
    content.current.scrollTop = top;
    if (content.current.scrollTop >= top - 1 || !hasMore) restoreScroll.current.top = 0;
    else if (!loadingMore && !error) loadMore();
  }, [searchKey, status, items, hasMore, loadingMore, error, loadMore, contentHeight, headerHeight, footerHeight, keyboardOpen]);

  const updateSearch = (nextQuery, nextKind = kind) => {
    const next = new URLSearchParams(location.search);
    if (nextQuery) next.set('q', nextQuery);
    else next.delete('q');
    if (nextKind === 'users') next.set('type', 'users');
    else { next.delete('type'); next.delete('blocked'); }
    navigate({ pathname: location.pathname, search: next.toString() }, {
      replace: true,
      state: { ...location.state, scrollTop: 0 },
    });
  };

  const closeSearch = () => {
    const origin = location.state?.searchOrigin;
    const validOrigin = typeof origin?.pathname === 'string' && origin.pathname.startsWith('/') && !origin.pathname.startsWith('//');
    navigate(validOrigin ? origin.pathname + (origin.search || '') : url(APP_ROUTES.MAIN), {
      replace: true,
      state: validOrigin ? origin.state : undefined,
    });
  };

  const openResult = (target, storyPreview) => {
    const searchReturn = { ...location.state, scrollTop: content.current?.scrollTop || 0 };
    // Update the existing entry as well: browser Back must restore the same
    // list position as the explicit Back button in the opened result.
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: searchReturn });
    navigate(target, {
      state: {
        from: location.pathname + location.search,
        searchReturn,
        ...(storyPreview ? { storyPreview } : {}),
      },
    });
  };

  const addEvent = () => {
    const target = `${url(APP_ROUTES.STORY_ADD)}${query.trim() ? `?title=${encodeURIComponent(query.trim())}` : ''}`;
    openResult(getUser() ? target : `${url(APP_ROUTES.LOGIN)}?from=${encodeURIComponent(target)}`);
  };

  const changeTab = (nextKind) => {
    if (kind !== nextKind) updateSearch(query, nextKind);
  };

  const handleTabKey = (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextKind = event.key === 'Home' ? 'stories' : event.key === 'End' ? 'users' : kind === 'stories' ? 'users' : 'stories';
    changeTab(nextKind);
    (nextKind === 'stories' ? eventTab : peopleTab).current?.focus();
  };

  const addSuggestion = <div className="search-add-suggestion">
    <p>{t('searchAddPrompt')}</p>
    <button type="button" className="search-icon-button" aria-label={t('searchAddEvent')} title={t('searchAddEvent')} onClick={addEvent}>
      <NewsAddIcon />
    </button>
  </div>;

  return <Layout className="app-layout search-page" onKeyDown={event => {
    if (event.key === 'Escape' && !event.nativeEvent.isComposing && !composing) {
      event.preventDefault();
      closeSearch();
    }
  }}>
    <Helmet><title>{t('searchTitle')} — WIW</title></Helmet>
    <Header className="header" ref={header}>
      <div className="search-page-controls">
        <div className="search-page-top">
          <button type="button" className="search-page-back" aria-label={t('searchBack')} onClick={closeSearch}><span><BackIcon /></span></button>
          <div className="search-page-field">
            <input ref={input} type="search" value={query} aria-label={t('searchTitle')}
              placeholder={t('searchPlaceholder')} autoComplete="off" spellCheck={false} enterKeyHint="search"
              aria-controls={`${tabId}-results`} aria-describedby={validationHint ? `${tabId}-hint` : undefined}
              onChange={event => updateSearch(event.target.value)}
              onCompositionStart={() => setComposing(true)} onCompositionEnd={() => setComposing(false)}
              onKeyDown={event => {
                if (event.nativeEvent.isComposing || composing) return;
                if (event.key === 'Enter') { event.preventDefault(); input.current?.blur(); }
              }} />
            <button type="button" className="search-icon-button search-clear" aria-label={t('searchClear')}
              tabIndex={query ? 0 : -1} style={{ visibility: query ? 'visible' : 'hidden' }}
              onClick={() => { updateSearch(''); input.current?.focus({ preventScroll: true }); }}><CloseIcon /></button>
          </div>
        </div>
        <div className="search-types" role="tablist" aria-label={t('searchTitle')} onKeyDown={handleTabKey}>
          <button type="button" ref={eventTab} id={`${tabId}-stories`} className="search-type" data-kind="stories" role="tab"
            aria-label={t('searchEvents')} title={t('searchEvents')} aria-selected={kind === 'stories'} aria-controls={`${tabId}-results`}
            tabIndex={kind === 'stories' ? 0 : -1} onClick={() => changeTab('stories')}><EventIcon /></button>
          <button type="button" ref={peopleTab} id={`${tabId}-users`} className="search-type search-type--users" data-kind="users" role="tab"
            aria-label={t('searchPeople')} title={t('searchPeople')} aria-selected={kind === 'users'} aria-controls={`${tabId}-results`}
            tabIndex={kind === 'users' ? 0 : -1} onClick={() => changeTab('users')}><AccountIcon /></button>
        </div>
        {kind === 'users' && blockedUsers.count > 0 && <button type="button" className="blocked-users-filter"
          aria-pressed={blockedMode} onClick={() => {
            const next = new URLSearchParams(location.search);
            if (blockedMode) next.delete('blocked'); else next.set('blocked', '1');
            navigate({ pathname: location.pathname, search: next.toString() }, { replace: true, state: { ...location.state, scrollTop: 0 } });
          }}><BanIcon /><span>{blocking('blockedUsers')} ({blockedUsers.count})</span></button>}
      </div>
    </Header>
    <Content ref={content} className="content-layot search-results" style={{ height: contentHeight, top: contentTop }}
      id={`${tabId}-results`} role="tabpanel" aria-labelledby={`${tabId}-${kind}`} aria-busy={status === 'loading' || loadingMore} tabIndex={0}>
      <div className="search-status-announcement" role="status" aria-live="polite">
        {status === 'ready' && t('searchResultCount', { count: items.length })}
      </div>
      {validationHint && <p className="search-empty" id={`${tabId}-hint`}>{t(validationHint)}</p>}
      {searchable && !composing && status === 'loading' && <div className="search-loading"><BrandLoader compact label={t('searchLoading')} /></div>}
      {status === 'ready' && <>
        {items.length ? <ul className={`search-results-list search-results-list--${kind}`}>
          {items.map(item => <li key={item.id}>
            {kind === 'stories' ? <button type="button" className="search-event-card" lang={item.titleLanguage || undefined}
              onClick={() => openResult(url(`${APP_ROUTES.POST}/${item.id}/reactions`), { id: item.id, title: item.title })}>{item.title}</button>
              : <button type="button" className="search-person-result" onClick={() => openResult(url(`${APP_ROUTES.USER}/${encodeURIComponent(item.user_name)}`))}>
                <UserImage picture={item.picture} />
                <span className="search-person-copy"><strong>{item.first_name || item.user_name}</strong><span>@{item.user_name}</span></span>
                <BackIcon className="search-person-arrow" />
              </button>}
          </li>)}
        </ul> : <p className="search-empty">{blockedMode ? blocking(query ? 'blockedSearchEmpty' : 'blockedEmpty') : t(kind === 'stories' ? 'searchNoEvents' : 'searchNoPeople')}</p>}
      </>}
      {error && <LoadFailure onRetry={status === 'ready' ? loadMore : retry} />}
      {status === 'ready' && hasMore && !error && <div className="search-pagination" aria-busy={loadingMore}>
        <button type="button" className="search-text-button" disabled={loadingMore} aria-busy={loadingMore} onClick={loadMore}>{t('searchMore')}</button>
      </div>}
      {status === 'ready' && kind === 'stories' && addSuggestion}
    </Content>
    <Footer ref={footer} style={keyboardOpen ? { display: 'none' } : undefined}><ButtonPanelReaction /></Footer>
  </Layout>;
}
